import React from 'react';
import { Loader2, RefreshCw, Check, AlertCircle, Clock, XCircle } from 'lucide-react';
import { MonitorRun } from '../../../../types/monitoring';
import { Button } from '../../../ui/Button';
import { formatDateTime } from '../../../../utils/dateUtils';

interface MonitorRunHistoryProps {
  runs: MonitorRun[];
  isLoading: boolean;
  onRefresh: () => void;
}

export const MonitorRunHistory: React.FC<MonitorRunHistoryProps> = ({
  runs,
  isLoading,
  onRefresh,
}) => {
  const getRunStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Check className="w-3 h-3 text-success-600" />;
      case 'running':
        return <Loader2 className="w-3 h-3 text-primary-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-error-500" />;
      case 'pending':
        return <Clock className="w-3 h-3 text-warning-500" />;
      case 'canceled':
        return <XCircle className="w-3 h-3 text-neutral-500" />;
      default:
        return <div className="w-3 h-3 rounded-full bg-neutral-300" />;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[13px] font-semibold text-neutral-900">Recent runs</h4>
        <Button
          id="refresh-run-history"
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          aria-label="Refresh run history"
        >
          {isLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
        </div>
      ) : runs.length === 0 ? (
        <p className="text-[13px] text-neutral-500 py-4">No runs yet</p>
      ) : (
        <div className="space-y-2">
          {runs.slice(0, 5).map((run) => (
            <div key={run.id} className="flex items-center gap-3 py-2">
              {getRunStatusIcon(run.status)}
              <span className="text-[12px] text-neutral-600 flex-1">
                {formatDateTime(run.started_at)}
              </span>
              <span className="text-[12px] text-neutral-900 font-medium">
                {run.items_found} found
              </span>
              <span className="text-[12px] text-neutral-500">
                {run.items_added} added
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
